import { Download, Expand, Share2, X } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  downloadVaultFile,
  isVaultImage,
  readVaultFile,
  type VaultFile,
  vaultFileObject,
} from '../lib/vault-files';
import { ActionIconButton, Button, IconButton, Input } from './ui';

export function VaultAttachment({
  file,
  editing,
  busy,
  onBusy,
  onChange,
  onMessage,
}: {
  file?: VaultFile;
  editing: boolean;
  busy: boolean;
  onBusy: (busy: boolean) => void;
  onChange: (file?: VaultFile) => void;
  onMessage: (message: string) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  const [reading, setReading] = useState(false),
    [shareMessage, setShareMessage] = useState('');

  const reportShare = (message: string) => {
    setShareMessage(message);
    onMessage(message);
  };

  async function share() {
    if (!file) {
      return;
    }

    setShareMessage('');
    const data = { files: [vaultFileObject(file)] };

    if (!navigator.canShare?.(data)) {
      reportShare('File sharing is unavailable in this browser. Download the file to share it.');

      return;
    }

    try {
      await navigator.share(data);
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        reportShare('Could not share this file. Try downloading it.');
      }
    }
  }

  return (
    <section className="vault-attachment" aria-label="Attachment">
      {editing && (
        <label>
          Attach a file · up to 5 MB
          <Input
            type="file"
            disabled={busy}
            onChange={async event => {
              const input = event.currentTarget,
                picked = input.files?.[0];

              if (!picked) {
                return;
              }

              onBusy(true);
              setReading(true);

              try {
                const next = await readVaultFile(picked);

                onChange(next);
                onMessage('Attachment ready. Save entry to keep it and synchronize it.');
              } catch (error) {
                onMessage((error as Error).message || 'The file could not be read.');
              } finally {
                input.value = '';
                setReading(false);
                onBusy(false);
              }
            }}
          />
        </label>
      )}
      {reading && <p role="status">Reading attachment…</p>}
      {file && (
        <>
          <strong className="vault-filename">{file.name}</strong>
          {isVaultImage(file) && (
            <img className="vault-image-thumbnail" src={file.data} alt={file.name} />
          )}
          <div className="button-row">
            {isVaultImage(file) && (
              <IconButton label="Open image" onClick={() => dialog.current?.showModal()}>
                <Expand aria-hidden="true" />
              </IconButton>
            )}
            <IconButton label="Download attachment" onClick={() => downloadVaultFile(file)}>
              <Download aria-hidden="true" />
            </IconButton>
            <IconButton label="Share attachment" onClick={() => void share()}>
              <Share2 aria-hidden="true" />
            </IconButton>
            {editing && (
              <ActionIconButton
                action="trash"
                label="Remove attachment"
                disabled={busy}
                onClick={() => onChange(undefined)}
              />
            )}
          </div>
          <dialog className="vault-image-dialog" ref={dialog} aria-label={`Image: ${file.name}`}>
            <div className="section-heading">
              <strong>{file.name}</strong>
              <IconButton label="Close image" onClick={() => dialog.current?.close()}>
                <X aria-hidden="true" />
              </IconButton>
            </div>
            {isVaultImage(file) && <img src={file.data} alt={file.name} />}
            <p role="status">{shareMessage}</p>
            <div className="button-row">
              <Button type="button" onClick={() => void share()}>
                Share image
              </Button>
              <Button type="button" onClick={() => downloadVaultFile(file)}>
                Download image
              </Button>
            </div>
          </dialog>
        </>
      )}
    </section>
  );
}
